import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { getAdvertisingBanners } from "../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../services/indexMediaBaseUrl";

const AUTOPLAY_MS = 4500;

const AdvertisingBanners = () => {
  const { i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getAdvertisingBanners();
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];
        const active = list
          .filter((b) => b.is_active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setBanners(active);
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const goTo = useCallback(
    (newIndex) => {
      if (!banners.length) return;
      setFade(false);
      setTimeout(() => {
        setIndex(((newIndex % banners.length) + banners.length) % banners.length);
        setFade(true);
      }, 300);
    },
    [banners.length]
  );

  const startAutoplay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (banners.length <= 1) return;
    timerRef.current = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % banners.length);
        setFade(true);
      }, 300);
    }, AUTOPLAY_MS);
  }, [banners.length]);

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(timerRef.current);
  }, [startAutoplay]);

  const prev = () => {
    clearInterval(timerRef.current);
    goTo(index - 1);
    startAutoplay();
  };

  const next = () => {
    clearInterval(timerRef.current);
    goTo(index + 1);
    startAutoplay();
  };

  const goToDot = (i) => {
    clearInterval(timerRef.current);
    goTo(i);
    startAutoplay();
  };

  const getTitle = (b) =>
    isAr ? b.title_ar || b.title_en || "" : b.title_en || b.title_ar || "";

  const getDescription = (b) =>
    isAr
      ? b.description_ar || b.description_en || ""
      : b.description_en || b.description_ar || "";

  const formatDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString(lang, { month: "short", day: "numeric" });
    } catch {
      return d;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: { xs: 260, md: 420 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "grey.50",
        }}
      >
        <CircularProgress size={40} sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  if (error || !banners.length) return null;

  const current = banners[index];
  const img = getImageUrl(current.image_web || current.image);
  const title = getTitle(current);
  const desc = getDescription(current);
  const dateRange =
    current.start_date || current.end_date
      ? `${formatDate(current.start_date)} – ${formatDate(current.end_date)}`
      : "";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 280, sm: 360, md: 440, lg: 500 },
        overflow: "hidden",
        bgcolor: "#0a0a0a",
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: fade ? 1 : 0,
          transition: "opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.02) 35%, rgba(0,0,0,0.6) 100%)",
          },
        }}
      />

      {/* Content Overlay */}
      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          pb: { xs: 5, md: 6 },
        }}
      >
        {/* Section Badge — top-left */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: 14, md: 24 },
            left: { xs: 14, md: 28 },
            right: { xs: 14, md: 28 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              bgcolor: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(16px)",
              borderRadius: 3,
              px: 2,
              py: 0.8,
              border: "1px solid rgba(213, 170, 47, 0.3)",
            }}
          >
            <LocalOfferIcon sx={{ fontSize: { xs: 18, md: 22 }, color: "#e6c76a" }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "0.78rem", md: "0.9rem" },
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
            </Typography>
          </Box>

          {/* Counter */}
          {banners.length > 1 && (
            <Box
              sx={{
                bgcolor: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(16px)",
                borderRadius: 2,
                px: 2,
                py: 0.5,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 700, fontSize: "0.75rem" }}
              >
                {index + 1} / {banners.length}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Text Content */}
        <Box
          sx={{
            opacity: fade ? 1 : 0,
            transform: fade ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
            maxWidth: { xs: "100%", md: "60%" },
          }}
        >
          {title && (
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#fff",
                mb: 1,
                fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.4rem" },
                lineHeight: 1.2,
                textShadow: "0 4px 20px rgba(0,0,0,0.4)",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {title}
            </Typography>
          )}

          {desc && (
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.85)",
                mb: 2,
                fontWeight: 500,
                fontSize: { xs: "0.82rem", md: "1rem" },
                lineHeight: 1.6,
                maxWidth: 550,
                textShadow: "0 2px 10px rgba(0,0,0,0.25)",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {desc}
            </Typography>
          )}

          {dateRange && (
            <Chip
              icon={<CalendarTodayIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
              label={dateRange}
              size="small"
              sx={{
                bgcolor: "rgba(213, 170, 47, 0.85)",
                backdropFilter: "blur(8px)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.75rem",
                height: 28,
                "& .MuiChip-icon": { color: "#fff" },
              }}
            />
          )}
        </Box>
      </Container>

      {/* Arrow Navigation */}
      {banners.length > 1 && (
        <>
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              left: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              width: { xs: 38, md: 48 },
              height: { xs: 38, md: 48 },
              bgcolor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(213, 170, 47, 0.4)",
                borderColor: "rgba(213, 170, 47, 0.6)",
                transform: "translateY(-50%) scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
          </IconButton>

          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              right: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              width: { xs: 38, md: 48 },
              height: { xs: 38, md: 48 },
              bgcolor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(213, 170, 47, 0.4)",
                borderColor: "rgba(213, 170, 47, 0.6)",
                transform: "translateY(-50%) scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <ChevronRightIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
          </IconButton>
        </>
      )}

      {/* Dot Indicators */}
      {banners.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 12, md: 18 },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            display: "flex",
            gap: 1,
            bgcolor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(12px)",
            borderRadius: 3,
            px: 2,
            py: 1,
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {banners.map((_, i) => (
            <Box
              key={i}
              onClick={() => goToDot(i)}
              sx={{
                width: i === index ? 28 : 10,
                height: 10,
                borderRadius: 5,
                bgcolor: i === index ? "#d5aa2f" : "rgba(255,255,255,0.4)",
                cursor: "pointer",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: i === index ? "0 0 12px rgba(213, 170, 47, 0.6)" : "none",
                "&:hover": {
                  bgcolor: i === index ? "#d5aa2f" : "rgba(255,255,255,0.7)",
                  transform: "scale(1.2)",
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Bottom Golden Line */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg, transparent, #d5aa2f, #e6c76a, #d5aa2f, transparent)",
          zIndex: 3,
        }}
      />

      {/* Progress Bar */}
      {banners.length > 1 && (
        <Box
          key={index}
          sx={{
            position: "absolute",
            bottom: 3,
            left: 0,
            height: 3,
            bgcolor: "#d5aa2f",
            zIndex: 4,
            animation: `adProgress ${AUTOPLAY_MS}ms linear`,
            "@keyframes adProgress": {
              from: { width: "0%" },
              to: { width: "100%" },
            },
            opacity: 0.7,
          }}
        />
      )}
    </Box>
  );
};

export default AdvertisingBanners;
